import React, { useState, useEffect } from 'react';
import {
  ClockIcon,
  CalendarIcon,
  CogIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  BeakerIcon,
  AdjustmentsHorizontalIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import TreatmentTypesClient from './TreatmentTypesClient';
import FormSettingsManagement from './FormSettingsManagement';
import OnlineBookingSettings from './OnlineBookingSettings';

interface AppointmentSettings {
  id: string;
  default_appointment_duration: number;
  advance_booking_limit: number;
  cancellation_notice_period: number;
  time_slot_interval: number;
  buffer_time_between_appointments: number;
  enable_group_sessions: boolean;
  max_patients_per_group: number;
  send_reminder_emails: boolean;
  reminder_hours_before: number;
  language: string;
}

interface WorkingHours {
  id: string;
  day_of_week: number;
  day_name: string;
  is_working_day: boolean;
  start_time: string | null;
  end_time: string | null;
  has_split_shift: boolean;
  morning_start: string | null;
  morning_end: string | null;
  afternoon_start: string | null;
  afternoon_end: string | null;
}

interface BreakPeriod {
  id: string;
  name: string;
  break_type: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  day_of_week: number;
  day_name: string;
  is_recurring: boolean;
  applies_to_all_days: boolean;
  description: string;
}

interface HolidayPeriod {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  description: string;
  auto_cancel_appointments: boolean;
}

const AppointmentSettingsClient: React.FC = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // State for settings
  const [settings, setSettings] = useState<AppointmentSettings | null>(null);
  const [workingHours, setWorkingHours] = useState<WorkingHours[]>([]);
  const [breakPeriods, setBreakPeriods] = useState<BreakPeriod[]>([]);
  const [holidayPeriods, setHolidayPeriods] = useState<HolidayPeriod[]>([]);

  // Modal states - removed unused state variables for now

  // Load all data
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      // Load settings first (required), then load others in parallel
      await loadSettings();
      
      // Load optional data in parallel
      await Promise.all([
        loadWorkingHours(),
        loadBreakPeriods(),
        loadHolidayPeriods()
      ]);
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to load appointment settings: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await fetch('/api/appointments/settings/', {
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings);
      } else {
        const errorText = await response.text();
        throw new Error(`Failed to load settings: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      throw error;
    }
  };

  const loadWorkingHours = async () => {
    try {
      const response = await fetch('/api/appointments/working-hours/', {
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setWorkingHours(data.working_hours);
      } else {
        throw new Error(`Failed to load working hours: ${response.status}`);
      }
    } catch (error) {
      throw error;
    }
  };

  const loadBreakPeriods = async () => {
    try {
      const response = await fetch('/api/appointments/break-periods/', {
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setBreakPeriods(data.break_periods);
      }
    } catch {
      // Break periods might not exist yet - silently ignore
    }
  };

  const loadHolidayPeriods = async () => {
    try {
      const response = await fetch('/api/appointments/holiday-periods/', {
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setHolidayPeriods(data.holiday_periods);
      }
    } catch {
      // Holiday periods might not exist yet - silently ignore
    }
  };

  const updateSettings = async (newSettings: Partial<AppointmentSettings>) => {
    setSaving(true);
    try {
      const response = await fetch('/api/appointments/settings/update/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newSettings)
      });

      if (response.ok) {
        setSettings(prev => prev ? { ...prev, ...newSettings } : null);
        setMessage({ type: 'success', text: 'Settings updated successfully' });
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.error || 'Failed to update settings' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update settings' });
    } finally {
      setSaving(false);
    }
  };

  const updateWorkingHours = async (dayOfWeek: number, hoursData: Partial<WorkingHours>) => {
    setSaving(true);
    try {
      const response = await fetch('/api/appointments/working-hours/update/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          day_of_week: dayOfWeek,
          ...hoursData
        })
      });

      if (response.ok) {
        await loadWorkingHours();
        setMessage({ type: 'success', text: 'Working hours updated successfully' });
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.error || 'Failed to update working hours' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update working hours' });
    } finally {
      setSaving(false);
    }
  };

  const createBreakPeriod = async (breakData: any) => {
    setSaving(true);
    try {
      const response = await fetch('/api/appointments/break-periods/create/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(breakData)
      });
      
      if (response.ok) {
        await loadBreakPeriods();
        setMessage({ type: 'success', text: 'Break period created successfully' });
        return true; // Indicate success to the calling component
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.error || 'Failed to create break period' });
        return false;
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to create break period' });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const deleteBreakPeriod = async (breakId: string) => {
    if (!confirm('Are you sure you want to delete this break period?')) return;

    setSaving(true);
    try {
      const response = await fetch(`/api/appointments/break-periods/${breakId}/delete/`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        await loadBreakPeriods();
        setMessage({ type: 'success', text: 'Break period deleted successfully' });
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.error || 'Failed to delete break period' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete break period' });
    } finally {
      setSaving(false);
    }
  };

  // Clear message after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-3 text-muted-foreground">Loading appointment settings...</span>
      </div>
    );
  }

  const tabs = [
    { id: 'general', name: 'General Settings', icon: CogIcon },
    { id: 'hours', name: 'Working Hours', icon: ClockIcon },
    { id: 'breaks', name: 'Break Periods', icon: CalendarIcon },
    { id: 'holidays', name: 'Holiday Periods', icon: ExclamationTriangleIcon },
    { id: 'treatments', name: 'Treatment Types', icon: BeakerIcon },
    { id: 'form-customization', name: 'Form Customization', icon: AdjustmentsHorizontalIcon },
    { id: 'online-booking', name: 'Online Booking', icon: GlobeAltIcon },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-border pb-4">
        <h1 className="text-2xl font-bold text-card-foreground">Appointment Settings</h1>
        <p className="text-muted-foreground mt-1">
          Configure working hours, break periods, and appointment preferences
        </p>
      </div>

      {/* Success/Error Messages */}
      {message && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success' 
            ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300' 
            : 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300'
        }`}>
          <div className="flex items-center">
            {message.type === 'success' ? (
              <CheckIcon className="h-5 w-5 mr-2" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            )}
            {message.text}
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-border">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-card-foreground hover:border-muted'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="py-6">
        {activeTab === 'general' && (
          <GeneralSettings 
            settings={settings} 
            onUpdate={updateSettings} 
            saving={saving} 
          />
        )}

        {activeTab === 'hours' && (
          <WorkingHoursSettings 
            workingHours={workingHours} 
            onUpdate={updateWorkingHours} 
            saving={saving} 
          />
        )}

        {activeTab === 'breaks' && (
          <BreakPeriodsSettings 
            breakPeriods={breakPeriods}
            workingHours={workingHours}
            onCreateBreak={createBreakPeriod}
            onDeleteBreak={deleteBreakPeriod}
            saving={saving}
          />
        )}

        {activeTab === 'holidays' && (
          <HolidayPeriodsSettings 
            holidayPeriods={holidayPeriods}
            saving={saving}
          />
        )}

        {activeTab === 'treatments' && (
          <div className="bg-card rounded-lg border border-border p-6">
            <TreatmentTypesClient />
          </div>
        )}

        {activeTab === 'form-customization' && (
          <div className="bg-card rounded-lg border border-border p-6">
            <FormSettingsManagement />
          </div>
        )}

        {activeTab === 'online-booking' && (
          <div className="bg-card rounded-lg border border-border p-6">
            <OnlineBookingSettings />
          </div>
        )}
      </div>
    </div>
  );
};

// General Settings Component
const GeneralSettings: React.FC<{
  settings: AppointmentSettings | null;
  onUpdate: (settings: Partial<AppointmentSettings>) => void;
  saving: boolean;
}> = ({ settings, onUpdate }) => {
  if (!settings) return null;

  const handleChange = (field: keyof AppointmentSettings, value: any) => {
    onUpdate({ [field]: value });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Default Duration */}
        <div className="form-group">
          <label className="block text-sm font-medium mb-2 text-card-foreground">
            Default Appointment Duration (minutes)
          </label>
          <input
            type="number"
            value={settings.default_appointment_duration}
            onChange={(e) => handleChange('default_appointment_duration', parseInt(e.target.value))}
            className="form-input"
            min="5"
            max="240"
            step="5"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Standard duration for new appointments
          </p>
        </div>

        {/* Language Setting */}
        <div className="form-group">
          <label className="block text-sm font-medium mb-2 text-card-foreground">
            Calendar Language
          </label>
          <select
            value={settings.language || 'en'}
            onChange={(e) => handleChange('language', e.target.value)}
            className="form-input"
          >
            <option value="en">English (Sunday first)</option>
            <option value="fr">Français (Monday first)</option>
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            Calendar language and week start day
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Time Slot Interval */}
        <div className="form-group">
          <label className="block text-sm font-medium mb-2 text-card-foreground">
            Time Slot Interval (minutes)
          </label>
          <select
            value={settings.time_slot_interval}
            onChange={(e) => handleChange('time_slot_interval', parseInt(e.target.value))}
            className="form-input"
          >
            <option value={5}>5 minutes</option>
            <option value={10}>10 minutes</option>
            <option value={15}>15 minutes</option>
            <option value={20}>20 minutes</option>
            <option value={30}>30 minutes</option>
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            Interval between available time slots
          </p>
        </div>

        {/* Advance Booking Limit */}
        <div className="form-group">
          <label className="block text-sm font-medium mb-2 text-card-foreground">
            Advance Booking Limit (days)
          </label>
          <input
            type="number"
            value={settings.advance_booking_limit}
            onChange={(e) => handleChange('advance_booking_limit', parseInt(e.target.value))}
            className="form-input"
            min="1"
            max="365"
          />
          <p className="text-xs text-muted-foreground mt-1">
            How far in advance can appointments be booked
          </p>
        </div>

        {/* Buffer Time */}
        <div className="form-group">
          <label className="block text-sm font-medium mb-2 text-card-foreground">
            Buffer Time Between Appointments (minutes)
          </label>
          <input
            type="number"
            value={settings.buffer_time_between_appointments}
            onChange={(e) => handleChange('buffer_time_between_appointments', parseInt(e.target.value))}
            className="form-input"
            min="0"
            max="60"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Time buffer between consecutive appointments
          </p>
        </div>

        {/* Cancellation Notice */}
        <div className="form-group">
          <label className="block text-sm font-medium mb-2 text-card-foreground">
            Cancellation Notice Period (hours)
          </label>
          <input
            type="number"
            value={settings.cancellation_notice_period}
            onChange={(e) => handleChange('cancellation_notice_period', parseInt(e.target.value))}
            className="form-input"
            min="1"
            max="168"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Minimum notice required for appointment cancellation
          </p>
        </div>

        {/* Reminder Hours */}
        <div className="form-group">
          <label className="block text-sm font-medium mb-2 text-card-foreground">
            Reminder Hours Before Appointment
          </label>
          <input
            type="number"
            value={settings.reminder_hours_before}
            onChange={(e) => handleChange('reminder_hours_before', parseInt(e.target.value))}
            className="form-input"
            min="1"
            max="168"
            disabled={!settings.send_reminder_emails}
          />
          <p className="text-xs text-muted-foreground mt-1">
            When to send appointment reminders
          </p>
        </div>
      </div>

      {/* Group Session Settings */}
      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-medium text-card-foreground mb-4">Group Session Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="form-group">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={settings.enable_group_sessions}
                onChange={(e) => handleChange('enable_group_sessions', e.target.checked)}
                className="form-checkbox"
              />
              <span className="text-sm font-medium text-card-foreground">
                Enable Group Sessions
              </span>
            </label>
            <p className="text-xs text-muted-foreground mt-1">
              Allow multiple patients in the same appointment slot
            </p>
          </div>

          {settings.enable_group_sessions && (
            <div className="form-group">
              <label className="block text-sm font-medium mb-2 text-card-foreground">
                Maximum Patients per Group
              </label>
              <input
                type="number"
                value={settings.max_patients_per_group}
                onChange={(e) => handleChange('max_patients_per_group', parseInt(e.target.value))}
                className="form-input"
                min="2"
                max="10"
              />
            </div>
          )}
        </div>
      </div>

      {/* Email Settings */}
      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-medium text-card-foreground mb-4">Email Settings</h3>
        
        <div className="form-group">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={settings.send_reminder_emails}
              onChange={(e) => handleChange('send_reminder_emails', e.target.checked)}
              className="form-checkbox"
            />
            <span className="text-sm font-medium text-card-foreground">
              Send Appointment Reminder Emails
            </span>
          </label>
          <p className="text-xs text-muted-foreground mt-1">
            Automatically send email reminders to patients
          </p>
        </div>
      </div>
    </div>
  );
};

// Working Hours Settings Component
const WorkingHoursSettings: React.FC<{
  workingHours: WorkingHours[];
  onUpdate: (dayOfWeek: number, hours: Partial<WorkingHours>) => void;
  saving: boolean;
}> = ({ workingHours, onUpdate, saving }) => {
  const [editingDay, setEditingDay] = useState<number | null>(null);

  const handleDayUpdate = (dayOfWeek: number, updates: Partial<WorkingHours>) => {
    onUpdate(dayOfWeek, updates);
    setEditingDay(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-card-foreground">Weekly Schedule</h3>
        <p className="text-sm text-muted-foreground">
          Configure your clinic's working hours for each day of the week
        </p>
      </div>

      <div className="space-y-4">
        {workingHours.map((hours) => (
          <WorkingHoursDay
            key={hours.day_of_week}
            hours={hours}
            isEditing={editingDay === hours.day_of_week}
            onEdit={() => setEditingDay(hours.day_of_week)}
            onSave={(updates) => handleDayUpdate(hours.day_of_week, updates)}
            onCancel={() => setEditingDay(null)}
            saving={saving}
          />
        ))}
      </div>
    </div>
  );
};

// Individual Day Component
const WorkingHoursDay: React.FC<{
  hours: WorkingHours;
  isEditing: boolean;
  onEdit: () => void;
  onSave: (updates: Partial<WorkingHours>) => void;
  onCancel: () => void;
  saving: boolean;
}> = ({ hours, isEditing, onEdit, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState(hours);

  useEffect(() => {
    setFormData(hours);
  }, [hours]);

  const handleSave = () => {
    onSave(formData);
  };

  if (!isEditing) {
    return (
      <div className="flex items-center justify-between p-4 border border-border rounded-lg">
        <div className="flex items-center space-x-4">
          <div className="w-20">
            <span className="font-medium text-card-foreground">{hours.day_name}</span>
          </div>
          <div className="flex-1">
            {hours.is_working_day ? (
              <div className="text-sm text-card-foreground">
                {hours.has_split_shift ? (
                  <>
                    {hours.morning_start} - {hours.morning_end}, {hours.afternoon_start} - {hours.afternoon_end}
                  </>
                ) : (
                  <>
                    {hours.start_time} - {hours.end_time}
                  </>
                )}
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">Closed</span>
            )}
          </div>
        </div>
        <button
          onClick={onEdit}
          className="btn-secondary btn-sm"
        >
          <PencilIcon className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 border border-border rounded-lg space-y-4">
      <div className="flex items-center justify-between">
        <span className="font-medium text-card-foreground">{hours.day_name}</span>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary btn-sm"
          >
            Save
          </button>
          <button
            onClick={onCancel}
            disabled={saving}
            className="btn-secondary btn-sm"
          >
            Cancel
          </button>
        </div>
      </div>

      <div className="form-group">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={formData.is_working_day}
            onChange={(e) => setFormData({ ...formData, is_working_day: e.target.checked })}
            className="form-checkbox"
          />
          <span className="text-sm font-medium text-card-foreground">Working Day</span>
        </label>
      </div>

      {formData.is_working_day && (
        <>
          <div className="form-group">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.has_split_shift}
                onChange={(e) => setFormData({ ...formData, has_split_shift: e.target.checked })}
                className="form-checkbox"
              />
              <span className="text-sm font-medium text-card-foreground">Split Shift</span>
            </label>
          </div>

          {formData.has_split_shift ? (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-card-foreground">
                  Morning Session
                </label>
                <div className="flex space-x-2">
                  <input
                    type="time"
                    value={formData.morning_start || ''}
                    onChange={(e) => setFormData({ ...formData, morning_start: e.target.value })}
                    className="form-input"
                  />
                  <input
                    type="time"
                    value={formData.morning_end || ''}
                    onChange={(e) => setFormData({ ...formData, morning_end: e.target.value })}
                    className="form-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-card-foreground">
                  Afternoon Session
                </label>
                <div className="flex space-x-2">
                  <input
                    type="time"
                    value={formData.afternoon_start || ''}
                    onChange={(e) => setFormData({ ...formData, afternoon_start: e.target.value })}
                    className="form-input"
                  />
                  <input
                    type="time"
                    value={formData.afternoon_end || ''}
                    onChange={(e) => setFormData({ ...formData, afternoon_end: e.target.value })}
                    className="form-input"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium mb-2 text-card-foreground">
                Working Hours
              </label>
              <div className="flex space-x-2">
                <input
                  type="time"
                  value={formData.start_time || ''}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="form-input"
                />
                <input
                  type="time"
                  value={formData.end_time || ''}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className="form-input"
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Break Periods Settings Component
const BreakPeriodsSettings: React.FC<{
  breakPeriods: BreakPeriod[];
  workingHours: WorkingHours[];
  onCreateBreak: (breakData: any) => Promise<boolean>;
  onDeleteBreak: (breakId: string) => void;
  saving: boolean;
}> = ({ breakPeriods, workingHours, onCreateBreak, onDeleteBreak, saving }) => {
  const [showModal, setShowModal] = useState(false);

  const handleCreateBreak = async (breakData: any): Promise<boolean> => {
    const success = await onCreateBreak(breakData);
    if (success) {
      setShowModal(false);
    }
    return success;
  };

  // Group break periods by day
  const breaksByDay = breakPeriods.reduce((acc, breakPeriod) => {
    if (!acc[breakPeriod.day_of_week]) {
      acc[breakPeriod.day_of_week] = [];
    }
    acc[breakPeriod.day_of_week].push(breakPeriod);
    return acc;
  }, {} as Record<number, BreakPeriod[]>);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-card-foreground">Break Periods</h3>
          <p className="text-sm text-muted-foreground">
            Configure break periods during working hours when appointments cannot be scheduled
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Break Period
        </button>
      </div>

      <div className="space-y-4">
        {workingHours
          .filter(hours => hours.is_working_day)
          .map((hours) => {
            const dayBreaks = breaksByDay[hours.day_of_week] || [];
            
            return (
              <div key={hours.day_of_week} className="border border-border rounded-lg p-4">
                <h4 className="font-medium text-card-foreground mb-3">{hours.day_name}</h4>
                
                {dayBreaks.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No break periods configured</p>
                ) : (
                  <div className="space-y-2">
                    {dayBreaks.map((breakPeriod) => (
                      <div key={breakPeriod.id} className="flex items-center justify-between p-3 bg-muted/30 rounded">
                        <div className="flex items-center space-x-4">
                          <div className="text-sm font-medium text-card-foreground">
                            {breakPeriod.name}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {breakPeriod.start_time} - {breakPeriod.end_time}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            ({breakPeriod.duration_minutes} min)
                          </div>
                          {breakPeriod.description && (
                            <div className="text-xs text-muted-foreground">
                              {breakPeriod.description}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => onDeleteBreak(breakPeriod.id)}
                          disabled={saving}
                          className="btn-secondary btn-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
      </div>

      {/* Break Period Creation Modal */}
      {showModal && (
        <BreakPeriodModal
          workingHours={workingHours}
          onClose={() => setShowModal(false)}
          onSave={handleCreateBreak}
          saving={saving}
        />
      )}
    </div>
  );
};

// Break Period Modal Component
const BreakPeriodModal: React.FC<{
  workingHours: WorkingHours[];
  onClose: () => void;
  onSave: (breakData: any) => Promise<boolean>;
  saving: boolean;
}> = ({ workingHours, onClose, onSave, saving }) => {
  const [formData, setFormData] = useState({
    name: '',
    break_type: 'lunch',
    start_time: '',
    end_time: '',
    day_of_week: '',
    applies_to_all_days: false,
    is_recurring: true,
    description: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      day_of_week: formData.applies_to_all_days ? null : parseInt(formData.day_of_week)
    };
    
    await onSave(submitData);
  };

  const workingDays = workingHours.filter(hours => hours.is_working_day);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      
      <div className="relative bg-card rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <h3 className="text-lg font-medium text-card-foreground mb-4">Add Break Period</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="form-group">
            <label className="block text-sm font-medium mb-2 text-card-foreground">
              Break Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="form-input"
              placeholder="e.g., Lunch Break"
              required
            />
          </div>

          <div className="form-group">
            <label className="block text-sm font-medium mb-2 text-card-foreground">
              Break Type
            </label>
            <select
              value={formData.break_type}
              onChange={(e) => setFormData({ ...formData, break_type: e.target.value })}
              className="form-input"
            >
              <option value="lunch">Lunch Break</option>
              <option value="coffee">Coffee Break</option>
              <option value="meeting">Staff Meeting</option>
              <option value="maintenance">Equipment Maintenance</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="form-group">
              <label className="block text-sm font-medium mb-2 text-card-foreground">
                Start Time
              </label>
              <input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium mb-2 text-card-foreground">
                End Time
              </label>
              <input
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                className="form-input"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.applies_to_all_days}
                onChange={(e) => setFormData({ ...formData, applies_to_all_days: e.target.checked })}
                className="form-checkbox"
              />
              <span className="text-sm font-medium text-card-foreground">
                Apply to all working days
              </span>
            </label>
          </div>

          {!formData.applies_to_all_days && (
            <div className="form-group">
              <label className="block text-sm font-medium mb-2 text-card-foreground">
                Day of Week
              </label>
              <select
                value={formData.day_of_week}
                onChange={(e) => setFormData({ ...formData, day_of_week: e.target.value })}
                className="form-input"
                required={!formData.applies_to_all_days}
              >
                <option value="">Select a day</option>
                {workingDays.map((hours) => (
                  <option key={hours.day_of_week} value={hours.day_of_week}>
                    {hours.day_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label className="block text-sm font-medium mb-2 text-card-foreground">
              Description (Optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="form-input"
              rows={2}
              placeholder="Additional details about this break period"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex-1"
            >
              {saving ? 'Creating...' : 'Create Break Period'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Holiday Periods Settings Component
const HolidayPeriodsSettings: React.FC<{
  holidayPeriods: HolidayPeriod[];
  saving: boolean;
}> = ({ holidayPeriods }) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-card-foreground">Holiday Periods</h3>
          <p className="text-sm text-muted-foreground">
            Configure holiday periods when the clinic is closed
          </p>
        </div>
        <button className="btn-primary">
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Holiday Period
        </button>
      </div>

      {holidayPeriods.length === 0 ? (
        <div className="text-center py-8">
          <CalendarIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No holiday periods configured</p>
        </div>
      ) : (
        <div className="space-y-4">
          {holidayPeriods.map((holiday) => (
            <div key={holiday.id} className="flex items-center justify-between p-4 border border-border rounded-lg">
              <div>
                <h4 className="font-medium text-card-foreground">{holiday.name}</h4>
                <p className="text-sm text-muted-foreground">
                  {holiday.start_date} to {holiday.end_date}
                </p>
                {holiday.description && (
                  <p className="text-xs text-muted-foreground mt-1">{holiday.description}</p>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <button className="btn-secondary btn-sm">
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button className="btn-secondary btn-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30">
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AppointmentSettingsClient;
